/**
 * The matching console: what has been tried for a booking, and what ops can still do about it.
 *
 * **Every wave carries its frozen evidence.** Rank, score and distance are what the engine wrote
 * at send time, not a recomputation — ops retuning a weight tomorrow must not change the answer
 * to "why was this pandit offered the job".
 *
 * **A hard filter cannot be waived here, and there is no button that pretends otherwise.** Both
 * manual paths post to endpoints that re-run all nine filters and refuse by name; this screen
 * shows the server's refusal verbatim. Assigning a pandit who is suspended, unverified or already
 * booked would be exactly the trust failure this product exists to prevent, and the fix is the
 * underlying record.
 *
 * **The per-candidate exclusion diagnostic is back** (E52, closing CSR-031). The fixture version
 * of this screen listed every evaluated pandit with the filter that removed them, which was the
 * most useful thing on it, and the screen got *less* useful when it became real: `build_candidates`
 * returns survivors only. It was kept absent rather than approximated until an endpoint could
 * answer honestly, because a list of "pandits we think might have been excluded" would send ops
 * to fix records that were never the problem. `GET .../dispatch/candidates` now evaluates each
 * filter separately and names the FIRST one that removed each pandit; `GET .../match-explain`
 * walks one pandit through all five stages, because passing the filters is only the first of the
 * five ways somebody does not get offered.
 */
import { useState } from 'react';
import { Button, Note, StatusChip } from '../components/primitives';
import { DataTable } from '../components/data';
import { Input } from '../components/forms';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, FilterBar, ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { useBookingBoard } from '../lib/api/hooks/bookings';
import {
  useAssignBooking,
  useCandidateRoster,
  useDispatchHistory,
  useMatchExplain,
  useQualityPull,
  useSendManualWave,
  useSendWave,
} from '../lib/api/hooks/dispatch';
import { ceremonyAt, clockTo, statusLabel, statusTone } from './DispatchBoard';
import { useUrlSearch } from '../lib/filterCodecs';

/**
 * The states a wave, a hand-picked wave or a phone acceptance can still act on — the server's
 * `matching._DISPATCHABLE`. `no_provider_available` is listed on this screen (it is the recruiting
 * signal) but nothing here can move it: the server refuses every one of the three.
 */
export const DISPATCHABLE: ReadonlySet<string> = new Set(['requested', 'matching', 'acceptance_expired']);

/** The states where matching is still somebody's problem. */
const UNMATCHED = ['requested', 'matching', 'acceptance_expired', 'no_provider_available'];

/** An offer's status, in the words an operator uses about it. */
export function offerLabel(status: string): string {
  return status === 'lost_race' ? 'somebody else was faster' : status.replaceAll('_', ' ');
}

export function offerTone(status: string): 'success' | 'error' | 'warn' | 'neutral' | 'info' {
  if (status === 'accepted') return 'success';
  if (status === 'declined' || status === 'expired') return 'error';
  if (status === 'pending') return 'warn';
  return 'neutral';
}

/** Ids typed by hand: an operator reading them off a directory should not be fought by the field. */
/**
 * Words for the server's filter keys.
 *
 * ⛔ The console owns the WORDING; the server owns which filters exist, which one removed each
 * pandit, and which it could not evaluate. A key this build has no copy for is still shown — as
 * the key — because a filter quietly missing from this table is how somebody stops seeing the
 * reason their roster is empty.
 */
export const FILTER_COPY: Readonly<Record<string, string>> = {
  not_deleted: 'Profile deleted',
  profile_active: 'Profile not active',
  listed: 'Not listed',
  skill_confirmed: 'Skill not confirmed for this puja',
  identity_badge: 'Identity badge missing or lapsed',
  bank_badge: 'Bank badge missing or lapsed',
  not_suspended: 'Suspended by a quality action',
  serves_city: 'No active service area in this city',
  window_free: 'Not free for the whole window',
  not_blacked_out: 'Blacked out that day',
  not_on_pilgrimage: 'Paused for pilgrimage',
  samagri_capable: 'Cannot supply samagri',
  within_travel_box: 'Too far away',
  travel_radius: 'Outside their own travel radius',
  /* A row the server marked failed without naming a filter. A blank chip would read as a pass. */
  unnamed: 'Removed, reason not named',
};

/** Who fixes it. The point of the breakdown: four causes, four different desks. */
export const FILTER_OWNER: Readonly<Record<string, string>> = {
  skill_confirmed: 'supply',
  identity_badge: 'KYC',
  bank_badge: 'KYC',
  serves_city: 'supply',
  samagri_capable: 'supply',
  window_free: 'nobody — a real constraint',
  not_blacked_out: 'nobody — a real constraint',
  not_on_pilgrimage: 'nobody — a real constraint',
};

/** Where the engine stopped. Five stages, and only the first is about the filters. */
export const STAGE_COPY: Readonly<Record<string, string>> = {
  hard_filter: 'Removed by a hard filter',
  already_offered: 'Already offered this booking',
  offer_cap: 'Holding as many open offers as we allow',
  wave_cut: 'Qualified and ranked, but the wave did not reach them',
  offered: 'Offered',
};

export function filterLabel(key: string): string {
  return FILTER_COPY[key] ?? key;
}

export function parseIds(raw: string): number[] {
  return raw
    .split(/[\s,]+/)
    .map((part) => Number(part.trim()))
    .filter((n) => Number.isInteger(n) && n > 0);
}

export function MatchingConsole() {
  const { scope, toast } = useConsole();
  const [q, setQ] = useUrlSearch('matching');
  const [openId, setOpenId] = useState<number | null>(null);
  const [manualIds, setManualIds] = useState('');
  const [assignId, setAssignId] = useState('');
  const [confirm, setConfirm] = useState<'wave' | 'manual' | 'assign' | 'pull' | null>(null);

  const board = useBookingBoard({
    status: q ? [] : UNMATCHED,
    q: q || undefined,
    page: 1,
    page_size: 50,
  });
  const history = useDispatchHistory(openId);
  const roster = useCandidateRoster(openId);
  const [explainId, setExplainId] = useState<number | null>(null);
  const explain = useMatchExplain(openId, explainId);
  const sendWave = useSendWave();
  const sendManual = useSendManualWave();
  const assign = useAssignBooking();
  const pull = useQualityPull();

  const rows = board.data?.items ?? [];
  const fail = (e: unknown) => toast({ tone: 'error' as const, message: errorMessage(e) });
  const done = (message: string) => {
    setConfirm(null);
    toast({ tone: 'info', message });
  };

  if (openId !== null) {
    const batches = history.data?.batches ?? [];
    const responses = history.data?.responses ?? [];
    /* Names come from the roster already read below; a pandit it no longer lists keeps the id. */
    const names = new Map((roster.data?.rows ?? []).map((r) => [r.pandit_profile_id, r.display_name]));
    return (
      <div style={{ display: 'grid', gap: 'var(--sp-lg)', alignItems: 'start' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-md)' }}>
          <ScopeAction
            scope={scope}
            need="ops_dispatch"
            size="sm"
            variant="ghost"
            icon="arrow-left"
            onClick={() => setOpenId(null)}
          >
            Back to unmatched
          </ScopeAction>
          <span style={{ flex: 1 }} />
          {history.data && (
            <StatusChip
              tone={statusTone(history.data.status)}
              label={statusLabel(history.data.status)}
            />
          )}
        </div>

        {history.isPending && <LoadingState message="Reading the dispatch history…" />}
        {history.isError && (
          <ErrorState
            title="Could not read the dispatch history"
            cause={errorMessage(history.error)}
          />
        )}

        {history.isSuccess && batches.length === 0 && (
          <EmptyState
            icon="git-branch"
            title="No wave has gone out yet"
            body="Nobody has been offered this job. Send one, or assign somebody who rang in."
            inline
          />
        )}

        {batches.map((batch) => (
          <Section
            key={batch.id}
            title={`Wave ${batch.batch_number} · ${batch.offer_count} offered`}
            note={
              batch.outcome
                ? `${batch.strategy.replaceAll('_', ' ')} · closed ${batch.outcome}`
                : `${batch.strategy.replaceAll('_', ' ')} · still open`
            }
            pad={false}
          >
            <DataTable
              density="compact"
              columns={[
                { key: 'rank', label: 'Rank', align: 'right', mono: true },
                { key: 'pandit', label: 'Pandit', mono: true },
                { key: 'name', label: 'Name' },
                { key: 'score', label: 'Score', align: 'right', mono: true },
                { key: 'distance', label: 'Distance', align: 'right', mono: true },
                { key: 'status', label: 'Outcome' },
                { key: 'why', label: 'Reason' },
              ]}
              rows={(batch.offers ?? []).map((offer) => ({
                id: offer.id,
                rank: offer.rank_in_batch,
                pandit: `#${offer.pandit_profile_id}`,
                name: names.get(offer.pandit_profile_id) ?? '',
                score: Number(offer.score).toFixed(4),
                distance: offer.distance_km === null ? '—' : `${offer.distance_km} km`,
                status: (
                  <StatusChip tone={offerTone(offer.status)} label={offerLabel(offer.status)} />
                ),
                why: offer.decline_reason?.replaceAll('_', ' ') ?? '',
              }))}
            />
          </Section>
        ))}

        {responses.length > 0 && (
          <Section
            title="Every tap"
            note="Including the ones that achieved nothing — a pandit who tried is a pandit who is engaged"
            pad={false}
          >
            <DataTable
              density="compact"
              columns={[
                { key: 'pandit', label: 'Pandit', mono: true },
                { key: 'name', label: 'Name' },
                { key: 'what', label: 'Tapped' },
                { key: 'outcome', label: 'Result' },
                { key: 'via', label: 'Via' },
              ]}
              rows={responses.map((r) => ({
                id: r.id,
                pandit: `#${r.pandit_profile_id}`,
                name: names.get(r.pandit_profile_id) ?? '',
                what: r.response,
                outcome: <StatusChip tone={offerTone(r.outcome)} label={offerLabel(r.outcome)} />,
                via: r.responded_via.replaceAll('_', ' '),
              }))}
            />
          </Section>
        )}

        {/* ⛔ The question this screen is opened with, and the one it could not answer until E52:
            "there are forty pandits in Patna, why did only two get offered?" A per-pandit lookup
            cannot answer it — you would have to already know whom to ask about. */}
        <Section
          title="Why only these?"
          note="Every pandit with a service area in this city, and the first filter that removed them"
          pad={false}
        >
          {roster.isPending && <LoadingState message="Evaluating the roster…" />}
          {roster.isError && <ErrorState title="The roster could not be evaluated" cause={errorMessage(roster.error)} />}
          {roster.data && roster.data.rows.length === 0 && (
            <EmptyState
              title="No pandit has a service area in this city"
              body="Nobody was evaluated, so no filter removed anybody. That is a coverage problem rather than a matching one."
            />
          )}
          {roster.data && roster.data.rows.length > 0 && (
            <>
              <div style={{ padding: 'var(--sp-md) var(--sp-lg)', display: 'grid', gap: 'var(--sp-sm)' }}>
                <div style={{ display: 'flex', gap: 'var(--sp-sm)', flexWrap: 'wrap' }}>
                  {Object.entries(roster.data.failures_by_filter)
                    .sort((a, b) => b[1] - a[1])
                    .map(([key, count]) => (
                      <StatusChip
                        key={key}
                        tone="warn"
                        label={`${count} · ${filterLabel(key)}${FILTER_OWNER[key] ? ` → ${FILTER_OWNER[key]}` : ''}`}
                      />
                    ))}
                  <StatusChip
                    tone="success"
                    label={`${roster.data.rows.filter((r) => r.passed).length} passed every filter`}
                  />
                </div>
                {/* ⛔ The server's words, verbatim: which filters it did NOT evaluate, and what an
                    absent filter means. A caveat kept only in this file is lost with this file. */}
                <span style={{ fontSize: 'var(--fs-admin-colhead)', color: 'var(--c-ink3)' }}>
                  {roster.data.basis}
                </span>
              </div>
              <DataTable
                density="compact"
                columns={[
                  { key: 'pandit', label: 'Pandit', mono: true },
                  { key: 'name', label: 'Name' },
                  { key: 'verdict', label: 'First filter that removed them' },
                  { key: 'act', label: '' },
                ]}
                rows={roster.data.rows.map((row) => ({
                  /* ⛔ Keyed on the profile id. Two pandits can share a display name, and a
                     name-keyed row would silently merge them. */
                  id: row.pandit_profile_id,
                  pandit: `#${row.pandit_profile_id}`,
                  name: row.display_name,
                  verdict: row.passed ? (
                    <StatusChip tone="success" label="Passed every filter" />
                  ) : (
                    <StatusChip tone="warn" label={filterLabel(row.failed_on ?? 'unnamed')} />
                  ),
                  act: (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setExplainId(row.pandit_profile_id)}
                    >
                      Explain
                    </Button>
                  ),
                }))}
              />
            </>
          )}
        </Section>

        {explainId !== null && (
          <Section
            title={`What happened to #${explainId}`}
            note="Passing the filters is only the first of five ways a pandit does not get offered"
            actions={
              <Button size="sm" variant="ghost" onClick={() => setExplainId(null)}>
                Close
              </Button>
            }
          >
            {explain.isPending && <LoadingState message="Walking the stages…" />}
            {explain.isError && <ErrorState title="That pandit could not be explained" cause={errorMessage(explain.error)} />}
            {explain.data && (
              <div style={{ display: 'grid', gap: 'var(--sp-sm)' }}>
                <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'center', flexWrap: 'wrap' }}>
                  <StatusChip
                    tone={explain.data.stage === 'offered' ? 'success' : 'warn'}
                    label={STAGE_COPY[explain.data.stage] ?? explain.data.stage}
                  />
                  {explain.data.excluded_by && (
                    <span style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
                      {filterLabel(explain.data.excluded_by)}
                    </span>
                  )}
                  {explain.data.rank !== null && explain.data.rank !== undefined && (
                    <span style={{ fontSize: 'var(--fs-admin-cell)', color: 'var(--c-ink2)' }}>
                      Ranked {explain.data.rank} of {explain.data.candidate_count} · wave takes{' '}
                      {explain.data.wave_size}
                    </span>
                  )}
                </div>
                {explain.data.signals.length > 0 && (
                  <DataTable
                    density="compact"
                    columns={[
                      { key: 'signal', label: 'Signal' },
                      { key: 'value', label: 'Value', align: 'right' },
                      { key: 'weight', label: 'Weight', align: 'right' },
                      { key: 'contribution', label: 'Contributed', align: 'right' },
                    ]}
                    rows={explain.data.signals.map((signal) => ({
                      id: signal.key,
                      signal: signal.key,
                      value: signal.value.toFixed(3),
                      weight: signal.weight.toFixed(2),
                      contribution: signal.contribution.toFixed(3),
                    }))}
                  />
                )}
              </div>
            )}
          </Section>
        )}

        <Section title="What ops can do" note="Every action re-runs the nine hard filters">
          {history.data && !DISPATCHABLE.has(history.data.status) && (
            <Note>
              A booking that is {statusLabel(history.data.status)} is not waiting for a pandit, so
              no wave or phone acceptance can reach it.
            </Note>
          )}
          <div
            style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end', flexWrap: 'wrap' }}
          >
            {(!history.data || DISPATCHABLE.has(history.data.status)) && (
              <>
                <ScopeAction
                  scope={scope}
                  need="ops_dispatch"
                  size="sm"
                  icon="arrow-right"
                  onClick={() => setConfirm('wave')}
                >
              Send the next wave
            </ScopeAction>
            <Input
              label="Hand-picked wave — pandit ids"
              value={manualIds}
              onChange={setManualIds}
              placeholder="12, 44, 91"
              style={{ maxWidth: 220 }}
            />
            <ScopeAction
              scope={scope}
              need="ops_dispatch"
              size="sm"
              variant="secondary"
              disabled={parseIds(manualIds).length === 0}
              onClick={() => setConfirm('manual')}
            >
              Offer to these
            </ScopeAction>
            <Input
              label="Assign directly — pandit id"
              value={assignId}
              onChange={(v) => setAssignId(v.replace(/[^0-9]/g, ''))}
              inputMode="numeric"
              style={{ maxWidth: 180 }}
            />
            <ScopeAction
              scope={scope}
              need="ops_dispatch"
              size="sm"
              variant="secondary"
              disabled={!assignId}
              onClick={() => setConfirm('assign')}
            >
              Record a phone acceptance
            </ScopeAction>
              </>
            )}
            {/* Only offered when somebody actually holds the job — by the time a booking
                reaches the replacement queue it has already been unassigned, and there is
                nothing left to pull. */}
            {history.data?.assignment && (
              <ScopeAction
                scope={scope}
                need="ops_dispatch"
                size="sm"
                variant="secondary"
                icon="user-x"
                onClick={() => setConfirm('pull')}
              >
                Remove the assigned pandit
              </ScopeAction>
            )}
          </div>
        </Section>

        <ConfirmAction
          open={confirm === 'wave'}
          title="Send the next wave?"
          phrase="SEND"
          consequences={[
            'Offers go to the best-ranked qualified pandits who have not already been offered this booking.',
            'The wave expires on its own; nobody is committed to anything by sending it.',
          ]}
          confirmLabel="Send it"
          onCancel={() => setConfirm(null)}
          onConfirm={() =>
            sendWave.mutate(
              { bookingId: openId },
              {
                onSuccess: (wave) =>
                  done(
                    wave.batch
                      ? `Wave ${wave.batch.batch_number} sent to ${wave.batch.offer_count}.`
                      : `No wave sent — ${wave.reason?.replaceAll('_', ' ')}.`,
                  ),
                onError: fail,
              },
            )
          }
        />

        <ConfirmAction
          open={confirm === 'manual'}
          title={`Offer this job to ${parseIds(manualIds).length} chosen pandit${parseIds(manualIds).length === 1 ? '' : 's'}?`}
          phrase="OFFER"
          consequences={[
            'The ranking is yours to widen. The nine hard filters are not — anybody who fails one is refused by name and nobody is offered.',
            'This counts as one of the booking’s waves.',
          ]}
          confirmLabel="Offer it"
          onCancel={() => setConfirm(null)}
          onConfirm={() =>
            sendManual.mutate(
              { bookingId: openId, panditProfileIds: parseIds(manualIds) },
              {
                onSuccess: (wave) => {
                  setManualIds('');
                  done(
                    wave.batch
                      ? `Offered to ${wave.batch.offer_count}.`
                      : `Nothing sent — ${wave.reason?.replaceAll('_', ' ')}.`,
                  );
                },
                onError: fail,
              },
            )
          }
        />

        <ConfirmAction
          open={confirm === 'pull'}
          title={
            history.data?.assignment
              ? `Remove pandit #${history.data.assignment.pandit_profile_id} from this booking?`
              : ''
          }
          phrase="REMOVE"
          tone="error"
          reason="Why this pandit is being taken off the job"
          consequences={[
            'They lose the job immediately, and the booking enters the replacement queue — standby first, then a fresh wave.',
            'This is us taking work away from a person. The reason is written to the audit log against your name, and it is the only account they will ever get of it.',
          ]}
          confirmLabel="Remove them"
          onCancel={() => setConfirm(null)}
          onConfirm={(reason) =>
            history.data?.assignment &&
            pull.mutate(
              { assignmentId: history.data.assignment.id, reason },
              {
                onSuccess: () => done('Removed. The booking is in the replacement queue.'),
                onError: fail,
              },
            )
          }
        />

        <ConfirmAction
          open={confirm === 'assign'}
          title={`Record that pandit #${assignId} accepted?`}
          phrase="ASSIGN"
          tone="error"
          reason="Who rang, and what they agreed to"
          consequences={[
            'This assigns the booking as if they had tapped accept, and withdraws every other live offer.',
            'It is recorded against your name in the audit log, and the response is marked as taken on their behalf rather than as a tap.',
            'The nine hard filters still apply — an unverified or unavailable pandit is refused.',
          ]}
          confirmLabel="Record it"
          onCancel={() => setConfirm(null)}
          onConfirm={(reason) =>
            assign.mutate(
              { bookingId: openId, panditProfileId: Number(assignId), reason },
              {
                onSuccess: () => {
                  setAssignId('');
                  done('Assigned, and written to the audit log.');
                },
                onError: fail,
              },
            )
          }
        />
      </div>
    );
  }

  return (
    <>
      <FilterBar
        q={q}
        onQ={setQ}
        placeholder="BMP code — press /"
        searchId="matching-search"
      />

      {board.isPending && <LoadingState message="Reading the unmatched queue…" />}
      {board.isError && (
        <ErrorState title="Could not read the queue" cause={errorMessage(board.error)} />
      )}
      {board.isSuccess && rows.length === 0 && (
        <EmptyState
          icon="git-branch"
          title={q ? 'No booking with that code' : 'Everything has a pandit'}
          body={
            q
              ? 'The code is matched exactly, across every state.'
              : 'Nothing is waiting to be matched right now.'
          }
          inline
        />
      )}

      {rows.length > 0 && (
        <DataTable
          density="compact"
          onRowClick={(row) => setOpenId(Number(row.id))}
          columns={[
            { key: 'code', label: 'Booking', mono: true },
            { key: 'when', label: 'Ceremony', sort: 'asc' },
            { key: 'status', label: 'State' },
            { key: 'clock', label: 'Clock', align: 'right', mono: true },
          ]}
          rows={rows.map((b) => ({
            id: b.id,
            code: b.public_code,
            when: ceremonyAt(b.scheduled_start_at),
            status: <StatusChip tone={statusTone(b.status)} label={statusLabel(b.status)} />,
            clock: clockTo(b.scheduled_start_at),
          }))}
        />
      )}

      <Note>
        Bookings still looking for a pandit, soonest ceremony first. Backup promotion and the
        replacement flow are on the replacements queue.
      </Note>
    </>
  );
}
